import React from "react";
import { EmptyState, FenceArt, Icon, Portal } from "./registry";
/* ============================================================
   Fence Works — Building Codes Reference
   IRC/IBC baseline cards (lib/codes.ts) + a per-state "typical
   residential defaults" panel, a setback calculator, printable
   inspection checklists, and a recent-lookups rail. The state
   choice, checklist ticks and history persist on this device.
   ============================================================ */

/* Typical residential defaults by state. These are planning values that
   drive the calculator and the jurisdiction panel — every card says so and
   Permit Check (permits.tsx) is where the encoded municipal rules live. */
const STATE_RULES: Record<string, { name: string; front: number; side: number; setback: string; permit: string; fee: string; note: string }> = {
  federal:    { name: "IRC/IBC baseline", front: 4, side: 6, setback: "0–2 ft (local)", permit: "IRC R105.2 exempts fences ≤ 7 ft; local amendments vary", fee: "$50–$150", note: "The model code is the floor. Cities and counties amend heights, setbacks and permit thresholds — check the municipality before you quote." },
  WI:         { name: "Wisconsin", front: 4, side: 6, setback: "0–2 ft (municipal)", permit: "Municipal — Green Bay, Milwaukee, Madison and Appleton require a fence permit", fee: "$40–$75", note: "Most Brown County municipalities also mandate a pool barrier; corner-lot vision triangles are enforced. Run the exact town through Permit Check." },
  FL:         { name: "Florida", front: 4, side: 6, setback: "0–3 ft (county)", permit: "Most counties require a permit for any new fence", fee: "$50–$150", note: "Pool safety (§515) requires ONE of four options — barrier, cover, alarms or self-latching devices. A fence is one option, never a mandate." },
  TX:         { name: "Texas", front: 4, side: 8, setback: "0–2 ft (city)", permit: "Usually over 6–8 ft or in a front/side street setback", fee: "$25–$100", note: "Many Texas cities allow 8 ft rear/side privacy fences; HOA rules are often stricter than the city." },
  CA:         { name: "California", front: 3.5, side: 6, setback: "0–5 ft (city)", permit: "Over 7 ft, or any retaining component", fee: "$100–$300", note: "Front-yard fences are typically capped at 42 in; many cities allow 6 ft + 1 ft of lattice on side and rear lines." },
  ID:         { name: "Idaho", front: 4, side: 6, setback: "2 ft from line", permit: "Over 6–7 ft (Boise: over 6 ft)", fee: "$50–$150", note: "Corner lots carry a clear-vision triangle; irrigation easements are common along rear lines." },
};
const STATE_KEY = "fw_codes_state";
const RECENT_KEY = "fw_codes_recent";
const CHECK_KEY = "fw_codes_checklist";
const ls = {
  get: (k: string, d: any) => { try { const v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch (e) { return d; } },
  set: (k: string, v: any) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} },
};

function CodesPage() {
  const [open, setOpen] = React.useState<any>(null);
  const [q, setQ] = React.useState<any>("");
  const codes = window.VFP_CODES;
  const filtered = q ? codes.filter(c => (c.name + c.sub + JSON.stringify(c.sections)).toLowerCase().includes(q.toLowerCase())) : codes;
  const [ht, setHt] = React.useState<any>(6);
  const [where, setWhere] = React.useState<any>("rear");   // front | rear
  const [st, setSt] = React.useState<any>(() => { const v = ls.get(STATE_KEY, "federal"); return STATE_RULES[v] ? v : "federal"; });
  const [recent, setRecent] = React.useState<any[]>(() => ls.get(RECENT_KEY, []));
  const rules = STATE_RULES[st];
  React.useEffect(() => { ls.set(STATE_KEY, st); }, [st]);

  function openCode(c: any) {
    setOpen(c);
    setRecent((r) => { const next = [c.id, ...r.filter((x) => x !== c.id)].slice(0, 5); ls.set(RECENT_KEY, next); return next; });
  }
  const maxHere = where === "front" ? rules.front : rules.side;
  const overMax = ht > maxHere;
  const fmtFt = (n: number) => Number.isInteger(n) ? `${n} ft` : `${Math.floor(n)} ft ${Math.round((n % 1) * 12)} in`;

  return (
    <div className="page fade-up">
      <div className="settings-grid">
        {/* left */}
        <aside className="set-side">
          <div className="card pad">
            <div className="row between" style={{ marginBottom: 12 }}><span className="kicker">Your Jurisdiction</span><span className="chip teal" style={{ fontSize: 10, padding: "1px 7px" }}>{st === "federal" ? "IRC" : st}</span></div>
            {[["Max Height (Front)", fmtFt(rules.front)], ["Max Height (Side/Rear)", fmtFt(rules.side)], ["Setback", rules.setback], ["Permit Required", rules.permit], ["Permit Fee", rules.fee]].map(([k, v]) =>
              <div key={k} className="row between gap-8" style={{ padding: "7px 0", borderBottom: "1px solid var(--line)", fontSize: 13, alignItems: "flex-start" }}>
                <span className="muted" style={{ minWidth: 0, flexShrink: 0 }}>{k}</span><span className="tnum" style={{ fontWeight: 700, textAlign: "right" }}>{v}</span></div>)}
            <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>Typical residential defaults for {rules.name}. {rules.note}</p>
          </div>
          <div className="card pad">
            <div className="kicker" style={{ marginBottom: 10 }}>Permit Checklist</div>
            {["Site plan / survey", "Property line verification", "HOA approval (if applicable)", "Utility locate (811)"].map(t => <CheckRow key={t} label={t} storeKey={CHECK_KEY} />)}
          </div>
        </aside>

        {/* main */}
        <main>
          <h1 className="display" style={{ fontSize: 32, margin: 0 }}>Building Codes Reference</h1>
          <p className="muted" style={{ margin: "5px 0 18px" }}>IRC &amp; IBC code requirements for fences, railings, and staircases. Pick a state for typical residential defaults — Permit Check has the encoded municipal rules.</p>
          <div className="row gap-12" style={{ marginBottom: 20, flexWrap: "wrap" }}>
            <div className="input row gap-8" style={{ flex: 1, minWidth: 240, display: "flex", alignItems: "center" }}>
              <Icon name="search" size={16} className="muted" />
              <input value={q} onChange={e => setQ(e.target.value)} aria-label="Search codes" placeholder="Search codes by keyword, product, or requirement…" style={{ background: "none", border: 0, outline: "none", color: "var(--fg)", flex: 1, fontSize: 14 }} />
            </div>
            <select className="select" style={{ maxWidth: 240 }} value={st} onChange={e => setSt(e.target.value)} aria-label="Jurisdiction">
              {Object.keys(STATE_RULES).map(k => <option key={k} value={k}>{STATE_RULES[k].name}</option>)}
            </select>
          </div>
          {filtered.length === 0 && (
            <div className="card pad"><EmptyState icon="search" title="No codes match" sub={q ? `Nothing for "${q}" — try another keyword, product, or requirement.` : "Try another keyword, product, or requirement."} action={q ? { label: "Clear search", onClick: () => setQ("") } : undefined} /></div>
          )}
          <div className="codes-grid">
            {filtered.map(c => (
              <button key={c.id} className="code-card" onClick={() => openCode(c)}>
                <div className="row gap-12" style={{ marginBottom: 14 }}>
                  <div className="code-ico"><FenceArt type={c.glyph} width={48} height={40} /></div>
                  <div><div style={{ fontWeight: 700, fontSize: 16 }}>{c.name}</div><div className="muted" style={{ fontSize: 12.5 }}>{c.sub}</div></div>
                </div>
                <div className="code-stats">
                  {c.mini.map(([l, v], k) => <div key={k}><div className="spec-lbl">{l}</div><div className="spec-val">{v}</div></div>)}
                </div>
                <div className="row gap-8" style={{ margin: "12px 0 10px" }}>
                  {c.badges.map(b => <span key={b} className={"chip " + (b === "POOL CODE" ? "" : "violet")} style={b === "POOL CODE" ? { color: "var(--red)", borderColor: "var(--red-tint)", background: "var(--red-tint)", fontSize: 10.5, padding: "2px 7px" } : { fontSize: 10.5, padding: "2px 7px" }}>{b}</span>)}
                </div>
                <div className="row between" style={{ color: "var(--teal)", fontWeight: 700, fontSize: 13, borderTop: "1px solid var(--line)", paddingTop: 12 }}>
                  View Full Code Requirements <Icon name="arrow" size={14} />
                </div>
              </button>
            ))}
          </div>
        </main>

        {/* right */}
        <aside className="set-side">
          <div className="card pad">
            <div className="kicker" style={{ marginBottom: 12 }}>Height &amp; Setback Check</div>
            <label className="field-label" htmlFor="codes-ht">Fence height (ft)</label>
            <input id="codes-ht" className="input tnum" type="number" min={1} max={12} step={0.5} value={ht} onChange={e => setHt(+e.target.value || 0)} />
            <div className="row gap-8" style={{ marginTop: 10 }}>
              {[["front", "Front yard"], ["rear", "Side / rear"]].map(([v, l]) => (
                <button key={v} className={"chip" + (where === v ? " teal" : "")} style={{ cursor: "pointer" }} onClick={() => setWhere(v)} aria-pressed={where === v}>{l}</button>
              ))}
            </div>
            <div className="card" style={{ marginTop: 12, padding: 12, background: overMax ? "var(--amber-tint)" : "var(--green-tint)", border: 0 }}>
              <div className="row gap-8" style={{ fontSize: 13, fontWeight: 700, color: overMax ? "var(--amber)" : "var(--green)" }}>
                <Icon name={overMax ? "bolt" : "check"} size={15} />{overMax ? `Over the typical ${where === "front" ? "front-yard" : "side/rear"} max (${fmtFt(maxHere)})` : `Within the typical ${where === "front" ? "front-yard" : "side/rear"} max (${fmtFt(maxHere)})`}
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{rules.name}: permit {rules.permit.toLowerCase()}. Setback {rules.setback}.</div>
            </div>
          </div>
          <div className="card pad">
            <div className="kicker" style={{ marginBottom: 10 }}>Recent Lookups</div>
            {recent.length === 0
              ? <p className="muted" style={{ fontSize: 12, textAlign: "center", margin: 0 }}>Open a code card and it lands here</p>
              : <div style={{ display: "grid", gap: 4 }}>
                  {recent.map(id => codes.find(c => c.id === id)).filter(Boolean).map(c => (
                    <button key={c.id} className="quick-link" onClick={() => openCode(c)}><Icon name="book" size={15} /><span style={{ flex: 1, textAlign: "left" }}>{c.name}</span><Icon name="chevright" size={14} className="muted" /></button>
                  ))}
                </div>}
          </div>
        </aside>
      </div>

      {open && <CodeModal code={open} jurisdiction={rules} onClose={() => setOpen(null)} />}
    </div>
  );
}

function CheckRow({ label, storeKey }: any) {
  const key = storeKey ? `${storeKey}:${label}` : null;
  const [on, setOn] = React.useState<any>(() => (key ? !!ls.get(key, false) : false));
  const toggle = () => { const v = !on; setOn(v); if (key) ls.set(key, v); };
  return (
    <button className="check-row" onClick={toggle} role="checkbox" aria-checked={on}>
      <span className={"cbox" + (on ? " on" : "")}>{on && <Icon name="check" size={12} />}</span>
      <span style={{ fontSize: 13, color: on ? "var(--fg-3)" : "var(--fg-2)", textDecoration: on ? "line-through" : "none" }}>{label}</span>
    </button>
  );
}

/* Printable inspection checklist: every requirement row of the code as a
   tick box, with the jurisdiction's typical defaults at the top. Opens a
   print window (same pattern as the receipt / build sheet). */
function printInspectionChecklist(code: any, jurisdiction: any) {
  const esc = (s: any) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const rows = (code.sections || []).map((sec: any) => `
    <h3>${esc(sec.title)} <small>${esc(sec.tag || "")}</small></h3>
    <table>${(sec.rows || []).map(([k, v]: any) => `<tr><td class="box">☐</td><td>${esc(k)}</td><td class="val">${esc(v)}</td><td class="note">&nbsp;</td></tr>`).join("")}</table>`).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Inspection checklist — ${esc(code.name)}</title>
  <style>
    body{font:13px/1.45 -apple-system,Segoe UI,Roboto,sans-serif;color:#111;margin:32px;max-width:820px}
    h1{font-size:22px;margin:0 0 4px} h2{font-size:14px;color:#555;font-weight:500;margin:0 0 18px}
    h3{font-size:14px;margin:18px 0 6px} h3 small{color:#777;font-weight:500;margin-left:6px}
    table{width:100%;border-collapse:collapse;margin-bottom:6px} td{border-bottom:1px solid #ddd;padding:6px 6px;vertical-align:top}
    td.box{width:22px;font-size:16px} td.val{width:32%;font-family:ui-monospace,monospace;font-size:12px} td.note{width:22%}
    .juris{border:1px solid #ccc;border-radius:8px;padding:10px 12px;margin:0 0 14px;font-size:12.5px;display:grid;grid-template-columns:1fr 1fr;gap:4px 16px}
    .sig{margin-top:28px;display:grid;grid-template-columns:1fr 1fr;gap:24px} .sig div{border-top:1px solid #333;padding-top:6px;font-size:12px;color:#555}
    .fine{color:#777;font-size:11px;margin-top:16px} @media print{body{margin:14mm}}
  </style></head><body>
  <h1>Inspection checklist — ${esc(code.name)}</h1>
  <h2>${esc(code.sub || "")} · ${esc(jurisdiction.name)} typical defaults</h2>
  <div class="juris"><div><b>Front-yard max:</b> ${esc(jurisdiction.front)} ft</div><div><b>Side/rear max:</b> ${esc(jurisdiction.side)} ft</div><div><b>Setback:</b> ${esc(jurisdiction.setback)}</div><div><b>Permit:</b> ${esc(jurisdiction.permit)}</div></div>
  ${rows}
  <div class="sig"><div>Inspected by / date</div><div>Job address</div></div>
  <p class="fine">${esc(code.note || "")} Typical values — confirm with the local jurisdiction before install.</p>
  <script>setTimeout(function(){window.print()},250)<\/script></body></html>`;
  const w = window.open("", "_blank", "width=860,height=900");
  if (!w) return;
  w.document.write(html); w.document.close();
}

function CodeModal({ code, jurisdiction, onClose }: any) {
  React.useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <Portal>
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal modal-lg" role="dialog" aria-modal="true" aria-label={`${code.name} building codes`} onClick={e => e.stopPropagation()}>
        <div className="row between" style={{ marginBottom: 18 }}>
          <h2 className="display" style={{ fontSize: 22, margin: 0 }}>{code.name} — Building Codes</h2>
          <div className="row gap-8">
            <button className="btn btn-ghost btn-sm" onClick={() => printInspectionChecklist(code, jurisdiction)}><Icon name="clipboard" size={14} />Inspection Checklist</button>
            <button className="btn btn-ghost btn-sm" onClick={() => window.print()}><Icon name="printer" size={14} />Print</button>
            <button className="icon-btn" onClick={onClose} aria-label="Close"><Icon name="x" size={18} /></button>
          </div>
        </div>
        <div className="code-modal-grid">
          {/* visual + mini */}
          <div>
            <div className="card" style={{ background: "var(--surface-2)", padding: 0, overflow: "hidden", marginBottom: 12 }}>
              <FenceArt type={code.glyph} width={360} height={210} />
            </div>
            <div className="code-stats" style={{ gridTemplateColumns: "1fr 1fr" }}>
              {code.mini.map(([l, v], k) => <div key={k}><div className="spec-lbl">{l}</div><div className="spec-val">{v}</div></div>)}
            </div>
          </div>
          {/* sections */}
          <div className="code-sections">
            {code.sections.map((sec, si) => (
              <div key={si} style={{ marginBottom: 16 }}>
                <div className="row gap-8" style={{ marginBottom: 8, alignItems: "center" }}>
                  <h4 style={{ margin: 0, fontSize: 15 }}>{sec.title}</h4>
                  <span className="chip violet" style={{ fontSize: 10, padding: "1px 7px" }}>{sec.tag}</span>
                </div>
                <table className="code-table">
                  <tbody>
                    {sec.rows.map(([k, v], ri) => <tr key={ri}><td>{k}</td><td className="mono">{v}</td></tr>)}
                  </tbody>
                </table>
              </div>
            ))}
            <div className="card" style={{ padding: 13, borderLeft: "3px solid var(--teal)", background: "var(--surface-2)" }}>
              <p style={{ margin: 0, fontSize: 12.5, color: "var(--fg-2)" }}>{code.note}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
}

Object.assign(window, { CodesPage, CodeModal, CheckRow });

export { CodesPage, CheckRow, CodeModal };
